/**
 * «Tenemos una nueva versión para ti»: el aviso que no deja seguir hasta actualizar.
 *
 * Vive en la raíz del panel. Cada cinco minutos, y cada vez que la pestaña vuelve a estar visible,
 * pregunta qué versión está publicada (`version.json`); si no es la que corre, tapa el panel con un
 * solo botón. Es deliberadamente bloqueante (el founder, 13-sep-2026, con el ejemplo de AliClick): un
 * dropshipper que sigue en la versión vieja ve pantallas que ya no existen y reporta bugs ya
 * arreglados. Lo que se protege es su trabajo: el texto lo dice y el botón recarga en el acto.
 *
 * 🪤 NO ES UN MODAL DE POLARIS A PROPÓSITO: un modal se cierra con Escape, con la X o tocando afuera,
 * y acá cerrar es exactamente lo que no debe pasar. Es una capa fija con los tokens de siempre.
 */
import { useEffect, useState } from 'react';
import { BlockStack, Box, InlineStack, Text } from '@/lib/polaris';
import { AppButton } from '@/components/admin/AppButton';
import { actualizarAhora, CADA_MS, hayVersionNueva, traerLaVersionPublicada, versionQueCorre } from '@/lib/laVersionDelPanel';

export default function AvisoDeNuevaVersion() {
  const [hayNueva, setHayNueva] = useState(false);
  const [actualizando, setActualizando] = useState(false);

  useEffect(() => {
    const corre = versionQueCorre();
    if (corre === 'dev' || !import.meta.env.PROD) return; // sin deploy no hay con qué comparar
    let vivo = true;
    const revisar = async () => {
      if (!vivo || document.visibilityState === 'hidden') return;
      const publicada = await traerLaVersionPublicada();
      if (vivo && hayVersionNueva(corre, publicada)) setHayNueva(true);
    };
    // La primera revisión espera medio minuto: recién cargado, el panel ya ES la última versión.
    const primera = window.setTimeout(revisar, 30_000);
    const cada = window.setInterval(revisar, CADA_MS);
    const alVolver = () => { if (document.visibilityState === 'visible') void revisar(); };
    document.addEventListener('visibilitychange', alVolver);
    return () => { vivo = false; window.clearTimeout(primera); window.clearInterval(cada); document.removeEventListener('visibilitychange', alVolver); };
  }, []);

  if (!hayNueva) return null;
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="aviso-version-titulo"
      style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(0, 0, 0, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--p-space-400)' }}
    >
      <Box background="bg-surface" borderRadius="300" padding="600" shadow="600" maxWidth="440px" width="100%">
        <BlockStack gap="400">
          <Text as="h2" variant="headingLg" id="aviso-version-titulo">Tenemos una nueva versión para ti</Text>
          <Text as="p" variant="bodyMd" tone="subdued">
            Smarty se actualizó mientras trabajabas. Para seguir, hay que cargar la versión nueva: tarda unos segundos y no pierdes lo que ya guardaste.
          </Text>
          <InlineStack align="end">
            <AppButton variant="primary" loading={actualizando} onClick={() => { setActualizando(true); void actualizarAhora(); }}>Actualizar</AppButton>
          </InlineStack>
        </BlockStack>
      </Box>
    </div>
  );
}
